import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/api';
import { Shield, Check, X, UserCog, Trash2 } from 'lucide-react';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker } from '../../utils/localizedContent';


const ROLE_BADGE = {
  admin: 'bg-[#C9A962]/20 text-[#C9A962]',
  staff: 'bg-violet-500/20 text-violet-300',
  user:  'bg-[#3B82F6]/20 text-[#3B82F6]',
};

export default function AdminUsers({ users, setUsers, roles = [], lang, setMessage }) {
  const [assigningRole, setAssigningRole] = useState(null); // user_id
  const [selectedRole, setSelectedRole] = useState('user');
  const [selectedStaffRoleId, setSelectedStaffRoleId] = useState('');
  const [loading, setLoading] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // user_id pending delete confirmation
  const [search, setSearch] = useState('');

  const isPrimaryAdmin = (u) => u.email === 'admin@elrincondecamo.com';
  const canModifyUser = (u) => !isPrimaryAdmin(u);

  const l = createTextPicker(lang);
  const fullName = (u) => [u.name, u.last_name].filter(Boolean).join(' ').trim() || u.email || 'No name';
  const filteredUsers = search.trim()
    ? users.filter(u =>
        fullName(u).toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const handleBan = async (userId, banned) => {
    setLoading(`ban-${userId}`);
    try {
      await axios.put(`${API}/admin/users/${userId}/ban`, { banned });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, banned } : u));
      setMessage(banned ? l('Usuario suspendido', 'User suspended') : l('Usuario reactivado', 'User reactivated'));
    } catch (err) {
      setMessage(formatApiError(err));
    } finally {
      setLoading(null);
    }
  };

  const handleDelete = async (userId) => {
    setLoading(`delete-${userId}`);
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.user_id !== userId));
      setMessage(l('Usuario eliminado correctamente', 'User deleted successfully'));
      setConfirmDelete(null);
    } catch (err) {
      setMessage(formatApiError(err));
    } finally {
      setLoading(null);
    }
  };

  const openRoleAssign = (user) => {
    setAssigningRole(user.user_id);
    setSelectedRole(user.role === 'admin' ? 'user' : (user.role || 'user'));
    setSelectedStaffRoleId(user.staff_role_id || '');
  };

  const cancelRoleAssign = () => {
    setAssigningRole(null);
    setSelectedRole('user');
    setSelectedStaffRoleId('');
  };

  const handleAssignRole = async (userId) => {
    if (selectedRole === 'staff' && !selectedStaffRoleId) {
      setMessage(l('Selecciona un rol personalizado', 'Please select a custom role'));
      return;
    }
    setLoading(`role-${userId}`);
    try {
      const payload = { role: selectedRole };
      if (selectedRole === 'staff') payload.staff_role_id = selectedStaffRoleId;
      await axios.put(`${API}/admin/users/${userId}/role`, payload);
      setUsers(prev => prev.map(u =>
        u.user_id === userId
          ? {
              ...u,
              role: selectedRole,
              staff_role_id: selectedRole === 'staff' ? selectedStaffRoleId : null,
              role_name: selectedRole === 'staff'
                ? roles.find(r => r.role_id === selectedStaffRoleId)?.name
                : undefined
            }
          : u
      ));
      setMessage(l('Rol asignado correctamente', 'Role assigned successfully'));
      cancelRoleAssign();
    } catch (err) {
      setMessage(formatApiError(err));
    } finally {
      setLoading(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <h2 className="text-white font-bold text-xl whitespace-nowrap">
          {l('Usuarios', 'Users')} ({users.length})
        </h2>
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={l('Buscar por nombre o email...', 'Search by name or email...')}
          className="flex-1 max-w-xs bg-[#12121A] border border-white/10 text-white rounded-xl px-4 py-2 text-sm outline-none focus:border-[#C9A962]"
        />
      </div>
      <div className="space-y-3">
        {filteredUsers.map(u => (
          <div
            key={u.user_id}
            className="bg-[#12121A] border border-white/5 rounded-xl p-4"
            data-testid={`admin-user-${u.user_id}`}
          >
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-[#C9A962]/20 flex items-center justify-center text-[#C9A962] font-bold flex-shrink-0">
                {(fullName(u) || '?')[0].toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{fullName(u)}</p>
                <p className="text-[#A1A1AA] text-sm truncate">{u.email}</p>
              </div>

              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {/* Role badge */}
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_BADGE[u.role] || ROLE_BADGE.user}`}>
                  {u.role === 'staff' && u.role_name ? u.role_name : u.role}
                </span>

                {/* Banned badge */}
                {u.banned && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-500/20 text-red-400">
                    {l('Suspendido', 'Suspended')}
                  </span>
                )}
              </div>

              {/* Actions (all non-primary-admin users) */}
              {canModifyUser(u) && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Ban/Unban */}
                  <button
                    onClick={() => handleBan(u.user_id, !u.banned)}
                    disabled={loading === `ban-${u.user_id}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                      u.banned
                        ? 'bg-[#10B981]/20 text-[#10B981] hover:bg-[#10B981]/30'
                        : 'bg-[#EF4444]/20 text-[#EF4444] hover:bg-[#EF4444]/30'
                    }`}
                  >
                    {loading === `ban-${u.user_id}`
                      ? '...'
                      : u.banned ? l('Reactivar', 'Unban') : l('Suspender', 'Ban')}
                  </button>

                  {/* Assign Role toggle */}
                  <button
                    onClick={() => assigningRole === u.user_id ? cancelRoleAssign() : openRoleAssign(u)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      assigningRole === u.user_id
                        ? 'bg-[#C9A962]/20 text-[#C9A962]'
                        : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                    }`}
                    title={l('Cambiar rol', 'Change role')}
                  >
                    <UserCog size={13} />
                    {assigningRole === u.user_id
                      ? l('Cancelar', 'Cancel')
                      : l('Cambiar Rol', 'Change Role')}
                  </button>

                  {/* Delete user */}
                  <button
                    onClick={() => setConfirmDelete(confirmDelete === u.user_id ? null : u.user_id)}
                    disabled={loading === `delete-${u.user_id}`}
                    className={`p-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                      confirmDelete === u.user_id
                        ? 'bg-red-500/30 text-red-300'
                        : 'bg-white/5 text-[#A1A1AA] hover:bg-red-500/20 hover:text-red-400'
                    }`}
                    title={l('Eliminar usuario', 'Delete user')}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* Delete Confirmation Panel */}
            {confirmDelete === u.user_id && (
              <div className="mt-3 pt-3 border-t border-red-500/20 flex items-center gap-3 flex-wrap">
                <p className="text-red-300 text-sm flex-1">
                  ⚠️ {l(
                    `¿Eliminar permanentemente a "${fullName(u)}"? Se borrarán sus reservas, colecciones y puntos de fidelidad.`,
                    `Permanently delete "${fullName(u)}"? Their reservations, collections and loyalty points will be removed.`
                  )}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDelete(u.user_id)}
                    disabled={loading === `delete-${u.user_id}`}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-red-500 text-white rounded-lg text-xs font-semibold hover:bg-red-600 transition-colors disabled:opacity-60"
                  >
                    <Trash2 size={13} />
                    {loading === `delete-${u.user_id}` ? l('Eliminando...', 'Deleting...') : l('Sí, eliminar', 'Yes, delete')}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(null)}
                    className="px-4 py-1.5 bg-white/5 text-[#A1A1AA] rounded-lg text-xs font-semibold hover:bg-white/10 transition-colors"
                  >
                    {l('Cancelar', 'Cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* Role Assignment Panel */}
            {assigningRole === u.user_id && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <p className="text-[#A1A1AA] text-xs font-medium mb-3">
                  {l('Asignar nuevo rol a', 'Assign new role to')} <span className="text-white">{fullName(u)}</span>
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Roles del sistema */}
                  <div>
                    <label className="block text-[#52525B] text-xs mb-1">{l('Rol del sistema', 'System role')}</label>
                    <select
                      value={selectedRole}
                      onChange={e => {
                        setSelectedRole(e.target.value);
                        setSelectedStaffRoleId('');
                      }}
                      className="bg-[#1C1C28] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A962]"
                    >
                      <option value="user">{l('Usuario normal', 'Regular user')}</option>
                      <option value="staff">{l('Staff (rol personalizado)', 'Staff (custom role)')}</option>
                      <option value="admin">🛡️ {l('Administrador', 'Administrator')}</option>
                    </select>
                  </div>

                  {/* Staff role selector */}
                  {selectedRole === 'staff' && (
                    <div>
                      <label className="block text-[#52525B] text-xs mb-1">{l('Rol personalizado', 'Custom role')}</label>
                      <select
                        value={selectedStaffRoleId}
                        onChange={e => setSelectedStaffRoleId(e.target.value)}
                        className="bg-[#1C1C28] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A962]"
                      >
                        <option value="">{l('-- Selecciona un rol --', '-- Select a role --')}</option>
                        {roles.map(r => (
                          <option key={r.role_id} value={r.role_id}>{r.name}</option>
                        ))}
                      </select>
                      {roles.length === 0 && (
                        <p className="text-xs text-[#EF4444] mt-1">
                          {l('No hay roles personalizados creados', 'No custom roles created yet')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Confirm */}
                  <div className="flex gap-2 mt-4 sm:mt-0 self-end">
                    <button
                      onClick={() => handleAssignRole(u.user_id)}
                      disabled={loading === `role-${u.user_id}`}
                      className="flex items-center gap-1.5 px-4 py-2 bg-[#C9A962] text-[#0A1612] rounded-lg text-sm font-semibold hover:bg-[#B8984E] transition-colors disabled:opacity-60"
                    >
                      <Check size={14} />
                      {loading === `role-${u.user_id}` ? '...' : l('Confirmar', 'Confirm')}
                    </button>
                    <button
                      onClick={cancelRoleAssign}
                      className="flex items-center gap-1.5 px-4 py-2 bg-white/5 text-[#A1A1AA] rounded-lg text-sm font-semibold hover:bg-white/10 transition-colors"
                    >
                      <X size={14} />
                      {l('Cancelar', 'Cancel')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
